import type { TechlineCaseEntity } from '@packages/domain/techline/cases';
import type { VinDetailEntity } from '@packages/domain/vin';

import { GoogleAnalyticsEvents } from '@web/core/google-analytics/google-analytics.events';
import { googleAnalyticsService } from '@web/services/singletons';
import { useEffect, useRef } from 'react';

import type { EditingWorksheet } from './use-techline-case';

/**
 * Parameters for the {@link useTechlineCaseAnalytics} hook.
 */
type useTechlineCaseAnalyticsParams = {
    /** The case number. */
    caseNumber: string | undefined;
    /** The Techline case data. */
    techlineCaseData: TechlineCaseEntity | undefined;
    /** The vehicle VIN data. */
    vehicleVinData: VinDetailEntity | undefined;
    /** The worksheet currently being edited, if any. */
    editingWorksheet: EditingWorksheet | null;
    /** The enriched worksheet group data. */
    editingWorksheetGroup: string | undefined;
};

/**
 * This hook tracks when a user opens a Techline case or a worksheet within that case,
 * and sends the appropriate events to Google Analytics.
 *
 * @param params - The parameters for the hook, including case number, case data, vehicle data, editing worksheet,
 * and enriched worksheet group data.
 */
export function useTechlineCaseAnalytics({
    caseNumber,
    techlineCaseData,
    vehicleVinData,
    editingWorksheet,
    editingWorksheetGroup,
}: useTechlineCaseAnalyticsParams): void {
    const trackedViewCaseRef = useRef<string | null>(null);
    const trackedWorksheetRef = useRef<string | null>(null);

    const techlineStatusCode = techlineCaseData?.techlineStatusCode;
    const primaryCaseType = techlineCaseData?.primaryCaseType;

    useEffect(() => {
        if (!caseNumber || !techlineStatusCode || !vehicleVinData || trackedViewCaseRef.current === caseNumber) {
            return;
        }
        trackedViewCaseRef.current = caseNumber;
        googleAnalyticsService.track(GoogleAnalyticsEvents.TechlineCaseOpen, {
            case_status: techlineStatusCode,
            case_type: primaryCaseType,
        });
    }, [caseNumber, techlineStatusCode, primaryCaseType, vehicleVinData]);

    useEffect(() => {
        if (!editingWorksheet) {
            trackedWorksheetRef.current = null;
            return;
        }
        const worksheetkey = editingWorksheet.submissionId || editingWorksheet.worksheetId;
        if (trackedWorksheetRef.current === worksheetkey) {
            return;
        }
        trackedWorksheetRef.current = worksheetkey;

        googleAnalyticsService.track(GoogleAnalyticsEvents.WorksheetOpen, {
            open_mode: editingWorksheet.mode,
            worksheet_group: editingWorksheetGroup,
        });
    }, [editingWorksheet, editingWorksheetGroup]);
}
